import { captureHandledException } from '@/lib/sentry';
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  getCompanyUserInvitations,
  updateCompany,
  getAppUsersByCompany,
  updateAppUser,
  addCompanyUserInvitation,
  addTrigger,
  Timestamp,
  writeCompanyAudit,
} from "@/lib/firebase";
import { Loader2, FlaskConical, Mail, Plus, X, Users } from "lucide-react";
import { logger } from "@/lib/logger";
import { useAuth } from "@/contexts/use-auth";

interface TrialInvitation {
  id: string;
  email: string;
  status: string;
  created?: any;
}

interface TrialCompany {
  id: string;
  name: string;
  type?: string;
  trialExpiryDate?: any;
}

interface EditTrialModalProps {
  isOpen: boolean;
  onClose: () => void;
  company: TrialCompany;
}

const validateEmail = (email: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const timestampToDateString = (ts: any): string => {
  if (!ts) return "";
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toISOString().split("T")[0];
  } catch (error) {
    captureHandledException(error, "trials.parse-date");
    return "";
  }
};

const formatDate = (ts: any): string => {
  if (!ts) return "—";
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch (error) {
    captureHandledException(error, "trials.parse-date");
    return "—";
  }
};

const statusVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
  const s = (status || "").toLowerCase();
  if (s === "pending") return "secondary";
  if (s === "sent" || s === "active") return "default";
  if (s === "error") return "destructive";
  return "outline";
};

export default function EditTrialModal({ isOpen, onClose, company }: EditTrialModalProps) {
  const [trialExpiryDate, setTrialExpiryDate] = useState("");
  const [newEmails, setNewEmails] = useState<string[]>([]);
  const [existingInvitations, setExistingInvitations] = useState<TrialInvitation[]>([]);
  const [loadingInvitations, setLoadingInvitations] = useState(false);
  const [invitationsError, setInvitationsError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    if (isOpen) {
      setTrialExpiryDate(timestampToDateString(company.trialExpiryDate));
      setNewEmails([]);
      setErrors({});
      setInvitationsError(false);
      fetchInvitations();
    }
  }, [isOpen, company]);

  const fetchInvitations = async () => {
    setLoadingInvitations(true);
    setInvitationsError(false);
    try {
      const invitations: TrialInvitation[] = (await getCompanyUserInvitations(company.id) as any[]).map(d => ({
        id: d.id,
        email: d.email || "",
        status: d.status || "Pending",
        created: d.created,
      }));
      invitations.sort((a, b) => a.email.localeCompare(b.email));
      setExistingInvitations(invitations);
    } catch (error: any) {
      logger.error("❌ Error loading invitations:", error);
      setInvitationsError(true);
    } finally {
      setLoadingInvitations(false);
    }
  };

  const addEmailField = () => setNewEmails([...newEmails, ""]);

  const removeEmailField = (index: number) => {
    setNewEmails(newEmails.filter((_, i) => i !== index));
  };

  const updateEmail = (index: number, value: string) => {
    const updated = [...newEmails];
    updated[index] = value;
    setNewEmails(updated);
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!trialExpiryDate) {
      newErrors.trialExpiryDate = "Trial expiry date is required";
    }

    const filledEmails = newEmails.filter(e => e.trim());
    if (filledEmails.length > 0) {
      const invalid = filledEmails.find(e => !validateEmail(e.trim()));
      if (invalid) newErrors.emails = `Invalid email address: ${invalid}`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const expiryTimestamp = Timestamp.fromDate(new Date(trialExpiryDate));

      await updateCompany(company.id, { trialExpiryDate: expiryTimestamp });
      logger.log("✅ Trial expiry updated for company:", company.id);

      const appUsers = await getAppUsersByCompany(company.id) as any[];
      await Promise.all(appUsers.map(u => updateAppUser(u.id, { trialExpiryDate: expiryTimestamp })));
      logger.log("✅ Updated trialExpiryDate for", appUsers.length, "appUsers");

      const filledEmails = newEmails.filter(e => e.trim());

      const auditChanges: { field: string; from: string; to: string }[] = [];
      const oldExpiry = timestampToDateString(company.trialExpiryDate);
      if (trialExpiryDate !== oldExpiry) {
        auditChanges.push({ field: 'trialExpiryDate', from: formatDate(company.trialExpiryDate), to: trialExpiryDate });
      }
      if (filledEmails.length > 0) {
        auditChanges.push({ field: 'usersAdded', from: '', to: filledEmails.join(', ') });
      }
      await writeCompanyAudit(company.id, company.name, 'trial_updated', user?.email ?? 'unknown', user?.uid ?? '', auditChanges);

      for (const email of filledEmails) {
        await addCompanyUserInvitation({
          companyId: company.id,
          email: email.trim(),
          role: "user",
          status: "Pending",
          inviteDate: null,
          error: "",
          sent: false,
          created: Timestamp.now(),
        });
      }
      if (filledEmails.length > 0) {
        logger.log("✅ Added", filledEmails.length, "new trial user invitation(s)");

        await addTrigger({
          type: "inviteUsers",
          status: "Open",
          referenceId: company.id,
          created: Timestamp.now(),
        });
        logger.log("✅ Trigger created for inviteUsers:", company.id);
      }

      toast({
        title: "Trial updated",
        description: filledEmails.length > 0
          ? `Trial expiry updated and ${filledEmails.length} new user invitation(s) added.`
          : "Trial expiry date has been updated.",
      });

      onClose();
    } catch (error: any) {
      logger.error("❌ Error updating trial:", error);
      toast({
        title: "Failed to update trial",
        description: error.message || "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FlaskConical className="h-5 w-5" />
            Edit Trial — {company.name}
          </DialogTitle>
          <DialogDescription>
            Update the trial expiry date and optionally add more trial users.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="editTrialExpiryDate">
              Trial Expiry Date <span className="text-red-500">*</span>
            </Label>
            <Input
              id="editTrialExpiryDate"
              type="date"
              value={trialExpiryDate}
              onChange={(e) => setTrialExpiryDate(e.target.value)}
              disabled={isSubmitting}
              className={errors.trialExpiryDate ? "border-red-500" : ""}
            />
            {errors.trialExpiryDate && (
              <p className="text-sm text-red-600">{errors.trialExpiryDate}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-1.5">
              <Users className="h-4 w-4" />
              Trial Users
            </Label>
            {loadingInvitations ? (
              <div className="flex items-center gap-2 text-sm text-slate-500 py-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading users...
              </div>
            ) : invitationsError ? (
              <p className="text-sm text-red-600 py-1">Could not load existing invitations. Please close and reopen the modal to retry.</p>
            ) : existingInvitations.length === 0 ? (
              <p className="text-sm text-slate-400 py-1">No users invited yet.</p>
            ) : (
              <div className="rounded-md border divide-y">
                {existingInvitations.map(inv => (
                  <div key={inv.id} className="flex items-center justify-between px-3 py-2 gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <Mail className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                      <span className="text-sm truncate">{inv.email}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs text-slate-400">{formatDate(inv.created)}</span>
                      <Badge variant={statusVariant(inv.status)} className="text-xs capitalize">
                        {inv.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>Add More Users</Label>
            <p className="text-xs text-slate-500">Enter email addresses to invite additional users to this trial.</p>
            <div className="space-y-2">
              {newEmails.map((email, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Mail className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => updateEmail(index, e.target.value)}
                      placeholder="user@company.com"
                      disabled={isSubmitting}
                      className={`pl-10 ${errors.emails ? "border-red-500" : ""}`}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-9 w-9 p-0 text-slate-400 hover:text-red-600"
                    onClick={() => removeEmailField(index)}
                    disabled={isSubmitting}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
            {errors.emails && <p className="text-sm text-red-600">{errors.emails}</p>}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addEmailField}
              disabled={isSubmitting}
              className="gap-1"
            >
              <Plus className="h-3 w-3" />
              Add user email
            </Button>
          </div>

          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
